/**
 * The whole first linking round, worked out in one go.
 *
 * `productSync.js` answers a question about one product ("which products over there could be
 * this one?"). This file answers the question that comes before it, once: given both
 * catalogues, what should the links be? That is a different job - it has to look at the
 * proposals *together*, because two products proposing the same counterpart is a problem no
 * per-product suggestion can see - so it lives in its own file rather than growing productSync
 * further.
 *
 * Nothing here writes anything. It computes a proposal; `useProductLinks.createLinks` applies
 * whatever the user ticked, and re-checks everything it is told, because between building this
 * plan and committing it someone else may have linked the same product by hand. Same split as
 * `buildSyncPlan` and `buildRouteSync`.
 *
 * ## Why one direction
 *
 * Proposals are made from one shop's products towards the other, VCE by default. A link is
 * one-to-one and symmetric, so running both directions would produce every pair twice - and
 * the two directions do not even agree (partial name matching is not symmetric in which name
 * is the longer one). VCE is the source because that is where `productLinks` lives and where
 * this CMS authenticates; products in the other shop that never get proposed are counted and
 * reported, not hidden.
 */

import {
  linkedProductIdsForShop,
  otherShop,
  SHOP_HVO,
  SHOP_VCE,
  suggestLinkCandidates,
} from "./productSync";

/** Certain enough to tick by default: the same document, or a pair a human already paired. */
export const CONFIDENCE_CERTAIN = "zeker";
/** The names say so. Very probably right, still ticked, still worth reading. */
export const CONFIDENCE_LIKELY = "waarschijnlijk";
/** A name that merely looks like it. Off by default - this is the one that links the wrong meat. */
export const CONFIDENCE_UNSURE = "onzeker";

/** Strongest first; the overview renders its sections in this order. */
export const CONFIDENCE_ORDER = [CONFIDENCE_CERTAIN, CONFIDENCE_LIKELY, CONFIDENCE_UNSURE];

const CONFIDENCE_BY_REASON = {
  id: CONFIDENCE_CERTAIN,
  pickRoute: CONFIDENCE_CERTAIN,
  name: CONFIDENCE_LIKELY,
  compact: CONFIDENCE_LIKELY,
  partial: CONFIDENCE_UNSURE,
};

/** Reason levels in ranking order, used to break a tie when counting the commonest one. */
const REASON_ORDER = ["id", "pickRoute", "name", "compact", "partial"];

/**
 * How much a match level is worth as a statement about identity.
 *
 * An id match and a pick route match are both "zeker": one is the same document, the other is
 * a pairing a human made by hand in the freezer. The two name levels are a strong hint but
 * still a hint. A partial name match is a guess, and a wrong guess here puts one product's
 * pick weight on a different piece of meat - so it is never ticked for you.
 */
export const confidenceForReason = (reason) => CONFIDENCE_BY_REASON[reason] || CONFIDENCE_UNSURE;

/** Two source products want the same counterpart; a one-to-one link cannot give it to both. */
export const CONFLICT_DUPLICATE_TARGET = "duplicate-target";

const describe = (product, shopSource) => ({
  shopSource,
  productId: product.id,
  name: product.name || "",
  product,
});

const countShop = (list, linkedIds) => {
  const linked = list.filter((product) => linkedIds.has(product.id)).length;
  return { total: list.length, linked, unlinked: list.length - linked };
};

const emptyCounts = (keys) => keys.reduce((counts, key) => ({ ...counts, [key]: 0 }), {});

/**
 * The commonest match level among the proposals, with ties going to the stronger level.
 *
 * This is the answer to the question the owner actually asked: *are* the document ids the
 * same? If almost every proposal comes out at "id", HVO really is a copy of VCE and this whole
 * round is a formality. If almost none do, the ids were regenerated and the names are all we
 * have - and the screen should say so rather than let someone tick 250 guesses.
 */
const commonestReason = (byReason, total) => {
  if (total === 0) return { topReason: null, topReasonCount: 0, topReasonShare: 0 };

  const top = REASON_ORDER.reduce(
    (best, reason) => (byReason[reason] > (best ? byReason[best] : 0) ? reason : best),
    null,
  );
  if (!top || byReason[top] === 0) return { topReason: null, topReasonCount: 0, topReasonShare: 0 };

  return { topReason: top, topReasonCount: byReason[top], topReasonShare: byReason[top] / total };
};

/**
 * Work out the links that should exist, without creating any of them.
 *
 * Per unlinked source product: the best candidate in the other shop, why it is the best one,
 * how much that is worth, and whether it should be ticked by default. Products that already
 * have a link are skipped entirely - they are settled, and re-proposing them would only
 * produce writes that `createLinks` has to refuse again.
 *
 * ## Conflicts
 *
 * Two source products can land on the same counterpart - two spellings of the same thing in
 * one shop, or a partial name that swallows a whole family of products. `createLink` guards
 * one-to-one against *stored* links, which says nothing about a second entry sitting in the
 * same batch, so that guard alone would let the first write win and the second fail (or worse,
 * both succeed if they were committed in the same batch). Both sides are therefore marked and
 * both are unticked: the right answer is for a human to pick one, not for the screen to pick
 * whichever it happened to sort first.
 *
 * @param {Object[]} products - every product of both shops, tagged with `shopSource`
 * @param {Object[]} links - the links that already exist
 * @param {Object[]} pickRoute - the pick route entries, for the pickRoute match level
 * @param {string} [sourceShop] - which shop the proposals run from (default VCE)
 */
export const buildLinkProposal = ({ products, links, pickRoute, sourceShop } = {}) => {
  const source = sourceShop === SHOP_HVO ? SHOP_HVO : SHOP_VCE;
  const target = otherShop(source);

  const all = (products || []).filter((product) => !!product && !!product.id);
  const sourceProducts = all.filter((product) => (product.shopSource || SHOP_VCE) === source);
  const targetProducts = all.filter((product) => (product.shopSource || SHOP_VCE) === target);

  const linkedSourceIds = linkedProductIdsForShop(links, source);
  const linkedTargetIds = linkedProductIdsForShop(links, target);

  const proposals = [];
  const unmatched = [];

  sourceProducts.forEach((product) => {
    if (linkedSourceIds.has(product.id)) return;

    const suggestions = suggestLinkCandidates({
      product,
      otherShopProducts: targetProducts,
      existingLinks: links,
      pickRoute,
    });

    if (suggestions.length === 0) {
      unmatched.push(describe(product, source));
      return;
    }

    const best = suggestions[0];
    const confidence = confidenceForReason(best.reason);

    proposals.push({
      // Both sides of the pair, not just the source product.
      //
      // This key carries the tick across recomputations, and the proposal *is* recomputed
      // while the modal is open - `useProducts` and `useProductLinks` both revalidate on
      // reconnect. A key of only the source product would survive a recomputation in which
      // the counterpart changed: someone else links the HVO product this row proposed, the
      // round runs again, and the same key now points at a different product - very possibly
      // a "partial" guess at "onzeker", which is precisely the level that is never ticked for
      // you. The tick would be inherited by a proposal nobody agreed to.
      //
      // With the target in the key, a changed counterpart is a different row: the old key
      // disappears and takes its tick with it, which is the safe direction.
      key: `${source}|${product.id}|${target}|${best.product.id}`,
      source: describe(product, source),
      target: describe(best.product, target),
      reason: best.reason,
      reasonLabel: best.reasonLabel,
      score: best.score,
      confidence,
      // A flag, not a veto: on an id match it is the one signal that the copy hypothesis does
      // not hold for this product, and it has to reach the person ticking the box.
      nameMismatch: !!best.nameMismatch,
      // How many other products in the target shop also matched. Shown so "er was er maar
      // één" and "er waren er zeven" do not look the same in the overview.
      alternatives: suggestions.length - 1,
      conflict: false,
      conflictReason: null,
      conflictWith: [],
      // Een naamafwijking is het enige signaal dat twee documenten met hetzelfde id tóch niet
      // hetzelfde product zijn - bijvoorbeeld omdat een id ooit hergebruikt is. Dat is precies
      // de rij die het verkeerde vlees koppelt, dus die vinken we niet voor. Hij blijft wel in
      // het voorstel staan, gemarkeerd, zodat iemand hem bewust kan aanzetten.
      preselected: confidence !== CONFIDENCE_UNSURE && !best.nameMismatch,
    });
  });

  // --- conflicts: one counterpart, more than one suitor -----------------------------------
  const byTarget = new Map();
  proposals.forEach((proposal) => {
    const group = byTarget.get(proposal.target.productId) || [];
    group.push(proposal);
    byTarget.set(proposal.target.productId, group);
  });

  let conflictTargets = 0;
  byTarget.forEach((group) => {
    if (group.length < 2) return;
    conflictTargets += 1;
    group.forEach((proposal) => {
      proposal.conflict = true;
      proposal.conflictReason = CONFLICT_DUPLICATE_TARGET;
      proposal.conflictWith = group
        .filter((other) => other !== proposal)
        .map((other) => other.source.name || other.source.productId);
      // Both off. Leaving the stronger one on would be a guess about which of two products is
      // the real one, made by sort order.
      proposal.preselected = false;
    });
  });

  proposals.sort(
    (a, b) =>
      b.score - a.score ||
      (a.source.name || "").localeCompare(b.source.name || "", "nl") ||
      a.source.productId.localeCompare(b.source.productId),
  );
  unmatched.sort(
    (a, b) => (a.name || "").localeCompare(b.name || "", "nl") || a.productId.localeCompare(b.productId),
  );

  const byReason = emptyCounts(REASON_ORDER);
  const byConfidence = emptyCounts(CONFIDENCE_ORDER);
  let preselected = 0;
  let conflicts = 0;
  let nameMismatches = 0;

  proposals.forEach((proposal) => {
    byReason[proposal.reason] = (byReason[proposal.reason] || 0) + 1;
    byConfidence[proposal.confidence] = (byConfidence[proposal.confidence] || 0) + 1;
    if (proposal.preselected) preselected += 1;
    if (proposal.conflict) conflicts += 1;
    if (proposal.nameMismatch) nameMismatches += 1;
  });

  return {
    sourceShop: source,
    targetShop: target,
    proposals,
    unmatched,
    summary: {
      sourceShop: source,
      targetShop: target,
      shops: {
        [source]: countShop(sourceProducts, linkedSourceIds),
        [target]: countShop(targetProducts, linkedTargetIds),
      },
      existingLinks: (links || []).length,
      /** Source products this round actually looked at: the ones not already linked. */
      considered: sourceProducts.length - countShop(sourceProducts, linkedSourceIds).linked,
      proposed: proposals.length,
      unmatched: unmatched.length,
      byConfidence,
      byReason,
      ...commonestReason(byReason, proposals.length),
      conflicts,
      conflictTargets,
      nameMismatches,
      preselected,
    },
  };
};

/** The proposals of one confidence level, in the order the plan put them. */
export const proposalsWithConfidence = (proposals, confidence) =>
  (proposals || []).filter((proposal) => proposal.confidence === confidence);

/** The keys that start ticked. Kept here so the screen and the tests agree on one answer. */
export const defaultSelectedKeys = (proposals) =>
  (proposals || []).filter((proposal) => proposal.preselected).map((proposal) => proposal.key);

/** Whether this proposal may be ticked at all. A conflict has to be resolved, not overruled. */
export const isSelectableProposal = (proposal) => !!proposal && !proposal.conflict;

/**
 * Turn chosen proposals into what `createLinks` wants: a VCE product and an HVO product per
 * link, whichever side the proposal was made from. `syncedProperties` is deliberately left
 * out, so the registry defaults are seeded in exactly one place (the hook) rather than two.
 */
export const linkEntriesForProposals = (proposals) =>
  (proposals || [])
    .filter(isSelectableProposal)
    .map((proposal) => {
      const sides = [proposal.source, proposal.target];
      const vce = sides.find((side) => side.shopSource === SHOP_VCE);
      const hvo = sides.find((side) => side.shopSource === SHOP_HVO);
      if (!vce || !hvo) return null;
      return { key: proposal.key, vceProduct: vce.product, hvoProduct: hvo.product };
    })
    .filter((entry) => !!entry);
